import { isNumber, pick } from 'radash';
import {
  assetsClipTranslateClipCreate,
  ClipAssetResponse,
  MediaContentResponse,
  MediaContent,
  cronSpeakerFaceCoordinatesCreate,
  SpeakerFaceCoordinatesResponse
} from '@goldcast/api/content';
import { getSceneChanges, ListActivityAPIQueryParams } from '@goldcast/api/backend';
import tinycolor from 'tinycolor2';
import { current } from 'immer';
import { core, isAudioContent } from './core';
import createStore from './store';
import { updateClipConfig } from './sharedAPI/sharedAPI';
import { initHistory, updateHistory } from './clipHistory';
import featureFlagStore from './featureFlagStore';
import {
  BACKGROUND_COLOR_DEFAULT,
  OUTLINE_COLOR_DEFAULT,
  TEXT_COLOR_DEFAULT,
  SPEAKER_LABEL_DEFAULT_TRANSITIONS
} from './constants';
import { migrateSpeakerLabelStyles } from './clipMigrationUtils/speakerLabelStyles.migration';
import { speakersAnalysisStore, getSpeakersAnalysis } from './speakersAnalysis/speakersAnalysis';
import { waitForClipToBeReady } from './clipAvailabilityStatus';
import {
  CaptionLineConfig,
  CaptionStyle,
  ClipMetadata,
  type CaptionStyles,
  type CaptionsPosition,
  type Clip,
  type HighlightType,
  type LayoutType,
  type SizeType
} from '@/domains/asset';
import { debounce, isEmptyObject, rgbaToHex } from '@/libs/utils';
import { generateThumbnail } from '@/libs/thumbnail';
import { loadClip } from '@/libs/clipContentUtil';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { showErrorToast, showInfoToast, showSuccessToast } from '@/libs/toast/toast';
import { PreviewTemplate } from '@/Pages/Clip/SideBar/types';
import { propertiesToCompareV1 } from '@/Pages/Clip/SideBar/Templates/utils';
import {
  getAnalyticsClipEditType,
  getClipAnalyticsPayload,
  getClipAnalyticsPayloadV2
} from '@/hooks/utils/analyticsHookUtil';
import { getLayoutAvailabilityStatus } from '@/context/ClipsContext/ClipsContextUtils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { getPlayerConfig } from '@/App/remotion/MainPlayer/utils/playerConfig/config';
import {
  AlignedRecordings,
  fetchAlignedRecordings,
  getAlignedRecordings
} from '@/App/remotion/MainPlayer/stores/alignedRecordings';
import {
  isNotFullLengthClip,
  isClipAudiogram,
  isClipFromRecordingStudio,
  isClipFromUploads,
  isFullLengthClip,
  isClipLiveEvent,
  isClipAlignedStreamAvailable,
  getClipFPS,
  getClipBroadcastId,
  doesClipHaveScenes,
  getClipReadOnlyPlayerConfig,
  doesClipHavePlayerConfig,
  isCustomUploadClip,
  isOriginalLayoutAvailable
} from '@/libs/clipUtils';
import { LanguageOption } from '@/Pages/Clip/CaptionTranslation/types';
import { Scenes } from '@/App/remotion/MainPlayer/types/Scene';
import { getCompositeSegment, getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { SceneChanges } from '@/domains/event';
import { FONT_DEFAULT_VALUE } from '@/Pages/Clip/SideBar/FontSelector/constants';
import {
  formSpeakerDetails,
  getSpeakersPayloadParams,
  getTranscript,
  loadSpeakers,
  populateSpeakersWithSpeakingSlots
} from '@/context/TranscriptContext/TranscriptContextUtils';
import {
  getSpeakerDetailsForBroadcast,
  updateSpeakerDetailsStoreForBroadcast
} from '@/App/remotion/MainPlayer/stores/speakerDetailsStore';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import { mergeDeletes } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';
import { DEFAULT_FONT_SIZE } from '@/App/remotion/libs/caption/styles';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';
import { generateVideoIdSpeakerMapFromSpeakers } from '@/App/remotion/MainPlayer/utils/playerConfig/compositeSegment/utils';
import { UPLOAD_SCREENSHARE_ID } from '@/App/remotion/MainPlayer/utils/playerConfig/compositeSegment/constants';
import { getCaptionStyles } from '@/Pages/Clip/CanvasPlayer/CanvasPlayerDraggableCaptions/utils';
import { CaptionPresetName } from '@/App/remotion/libs/caption/caption-presets/types';
import { LayoutType as LayoutTypeV2 } from '@/App/remotion/MainPlayer/utils/speakerLayoutGenerator/types';
import {
  isAlignedStreamAvailable,
  isLiveEventRecording,
  isLiveEventWithManualMigration,
  isRemotionMigrationPossibleForContent
} from '@/libs/mediaContentUtils';
import {
  getDefaultSceneData,
  rectifyScenes
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/sceneUtils';
import {
  RecordingVideoType,
  updateVideoToSpeakerDetailsStore
} from '@/App/remotion/MainPlayer/stores/videoSpeakerDetailsStore';
import { isSpeakerLayout } from '@/libs/layout';
import { CLIP_CUSTOMIZER_LAYOUT_TYPES } from '@/App/remotion/MainPlayer/utils/speakerLayoutGenerator/playground/options';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import {
  getAllCompositeSegments,
  getAllSegments
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { MAIN_TRACK_ID } from '@/App/remotion/MainPlayer/utils/playerConfig/constants';

export interface DeletedRange {
  next_start_time: number;
  prev_end_time: number;
  bounds: [start_time: number, end_time: number];
  deleteType?: 'FILLER_WORDS_MENU' | 'MANUAL' | 'AI_EDITS';
}

export interface EditedWord {
  word: string;
  start_time: number;
  end_time: number;
}

type CurrentClips = Record<string, Clip>;

type UpdateIntroOutroProps = {
  url?: string;
  duration?: number;
};

export const allClips = createStore<Clip[]>([]);
export const canGenerateClips = createStore<boolean>(true);

/**
 * @deprecated This store will no longer be meaningful once we move Fast Edit to 100%
 */
export const clipCaptionsLoader = createStore<Record<string, boolean>>({});

export const templateLoader = createStore<boolean>(false);
export const clipGeneratingCount = createStore<number>(0);
// loader for clip translation
export const clipTranslationLoader = createStore<Record<string, boolean>>({});

/**
 * Used to store boolean flags against clip ids of a freshly loaded clip which don't have download configs.
 * The download configs are generated and applied after a clip is loaded along with its other dependencies like speaker analysis
 */
export const clipsNeedingConfigUpdateStore = createStore<{
  [clipId: string]: boolean;
}>({});

function shouldDeleteClipConfig() {
  return !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Delete_Config];
}

function normalizeClipConfigForDeleteFlag<T extends Partial<Clip>>(clipData: T): T {
  if (!shouldDeleteClipConfig() || !clipData?.asset_metadata) {
    return clipData;
  }

  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      config: null
    }
  };
}

function normalizeClipMetadataConfigForDeleteFlag<T extends Partial<Clip['asset_metadata']>>(metadata: T): T {
  if (!shouldDeleteClipConfig()) {
    return metadata;
  }

  return {
    ...metadata,
    config: null
  };
}

/**
 * @deprecated This function will no longer be meaningful once we move Fast Edit to 100%
 */
export function updateClipCaptionsLoadingState({
  clipId,
  loading,
  forceRespectPayload = false
}: {
  clipId: string;
  loading: boolean;
  forceRespectPayload?: boolean;
}) {
  let finalLoadingState = false;

  if (forceRespectPayload) {
    finalLoadingState = loading;
  }

  clipCaptionsLoader.update(data => ({
    ...data,
    [clipId]: finalLoadingState
  }));
}

export function updateClipTranslationLoadingState(clipId: string, loading: boolean) {
  clipTranslationLoader.update(data => ({
    ...data,
    [clipId]: loading
  }));
}

/**
 * Used to render the integrated player and editor for a clip
 */
export const currentClip = createStore<CurrentClips>({});

function updateClipDefaultProperties(clip: Clip, facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse) {
  if (!clip.asset_metadata?.layout) {
    clip.asset_metadata = {
      ...(clip.asset_metadata || {}),
      layout: 'DEFAULT',
      size: 'LANDSCAPE'
    };
  }

  const coreStore = core.getSnapshot();
  if (clip.asset_metadata && !clip.asset_metadata.magicLayout) {
    clip.asset_metadata.magicLayout = {
      backgroundColor:
        rgbaToHex(coreStore.content?.project?.theme_obj?.theme_primary_color!) || BACKGROUND_COLOR_DEFAULT,
      showSpeakerLabels: true,
      textColor: rgbaToHex(coreStore.content?.project?.theme_obj.primary_text_color!) || TEXT_COLOR_DEFAULT
    };
  } else if (clip.asset_metadata?.magicLayout) {
    clip.asset_metadata.magicLayout = {
      ...clip.asset_metadata.magicLayout,
      backgroundColor: clip.asset_metadata.magicLayout.backgroundColor || BACKGROUND_COLOR_DEFAULT,
      textColor: clip.asset_metadata.magicLayout.textColor || TEXT_COLOR_DEFAULT
    };
  }

  if (clip.asset_metadata.hide_borders === undefined) {
    clip.asset_metadata.hide_borders = !clip.asset_metadata.subtitle;
  }

  if (isAudioContent()) {
    clip.asset_metadata.layout = 'AUDIOGRAM';
  }

  if (!clip.asset_metadata.fit) {
    clip.asset_metadata.fit = 'FIT';
  }

  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  if ((clip.asset_metadata.player_config || isSplitsEnabled) && (clip.asset_metadata.scenes || []).length === 0) {
    const newScenes = getDefaultSceneData({ clip: clip, facialRecognitionToDefaultPosition });
    clip.asset_metadata.scenes = [newScenes];
    const allCompositeSegments = getAllCompositeSegments(clip);
    allCompositeSegments.forEach(compositeSegment => {
      if (compositeSegment.segment_metadata) {
        compositeSegment.segment_metadata.scenes = [newScenes];
      }
    });
  }

  if (clip.asset_metadata.scenes) {
    const sortedScenes = clip.asset_metadata.scenes.sort((a, b) => a.fromFrame - b.fromFrame);
    const firstScene = sortedScenes[0];
    const lastScene = sortedScenes[sortedScenes.length - 1];
    const fps = getClipFPS(clip);
    if (
      firstScene &&
      firstScene.fromFrame !== clip.asset_metadata.start * fps &&
      firstScene.toFrame > clip.asset_metadata.start * fps
    ) {
      firstScene.fromFrame = clip.asset_metadata.start * fps;
    }
    if (
      lastScene &&
      lastScene.toFrame !== clip.asset_metadata.end * fps &&
      lastScene.fromFrame < clip.asset_metadata.end * fps
    ) {
      lastScene.toFrame = clip.asset_metadata.end * fps;
    }
  }

  if (Array.isArray(clip.asset_metadata?.captions)) {
    clip.asset_metadata.captions = clip.asset_metadata.captions.map((caption: any, index: number) => ({
      ...caption,
      index
    }));
  } else {
    clip.asset_metadata.captions = [];
  }

  // START - CR-4289 - captions-undo-fix
  // This code is same as in sharedAPI.ts. That is required for clip handling. This is required for history handling
  // Temp code. Will be cleaned after removal of shared api
  const showCaptions = !!clip.asset_metadata.subtitle;
  if (
    clip.asset_metadata.caption_positions === undefined &&
    clip.asset_metadata.caption_styles?.placement === undefined &&
    showCaptions &&
    clip.asset_metadata.layout !== 'AUDIOGRAM'
  ) {
    clip.asset_metadata = {
      ...clip.asset_metadata,
      caption_positions: {
        x: '2.5%',
        y: '82%',
        width: '95%',
        height: '18%'
      } as any
    };
  } else if (
    clip.asset_metadata.caption_positions &&
    typeof clip.asset_metadata.caption_positions?.height !== 'string' &&
    clip.asset_metadata.layout !== 'AUDIOGRAM' &&
    showCaptions
  ) {
    clip.asset_metadata = {
      ...clip.asset_metadata,
      caption_positions: {
        ...clip.asset_metadata.caption_positions,
        height: parseFloat(clip.asset_metadata.caption_positions.height as unknown as string) + '%',
        x: parseFloat(clip.asset_metadata.caption_positions.x as unknown as string) + '%',
        y: parseFloat(clip.asset_metadata.caption_positions.y as unknown as string) + '%',
        width: parseFloat(clip.asset_metadata.caption_positions.width as unknown as string) + '%'
      } as any
    };
  }
  // END - CR-4289 - captions-undo-fix

  if (!clip.asset_metadata.maxVideoDuration && clip.asset_metadata.is_full_length) {
    if (clip.asset_metadata.deletes) {
      const totalDeletes = Object.values(clip.asset_metadata.deletes).reduce(
        (acc, curr) => acc + (curr.bounds[1] - curr.bounds[0]),
        0
      );
      clip.asset_metadata.maxVideoDuration = (clip.asset_metadata.duration || 0) + totalDeletes;
    } else {
      clip.asset_metadata.maxVideoDuration = clip.asset_metadata.end;
    }
  }

  clip.asset_metadata.speaker_label_styles = migrateSpeakerLabelStyles(clip.asset_metadata.speaker_label_styles);

  // Migration of caption styles to new schema as per https://goldcast.atlassian.net/browse/CR-6425
  if (!clip.asset_metadata.caption_styles?.theme_preset) {
    clip.asset_metadata.caption_styles = {
      caps: clip.asset_metadata.caption_styles?.caps || false,
      font_size: clip.asset_metadata.caption_styles?.font_size || DEFAULT_FONT_SIZE,
      placement: clip.asset_metadata.caption_styles?.placement,
      line_config: clip.asset_metadata.caption_styles?.line_config || CaptionLineConfig.OneLine,
      font_location:
        clip.asset_metadata.caption_styles?.font_location ||
        clip.asset_metadata.font_location ||
        FONT_DEFAULT_VALUE.url,
      theme_preset: 'custom',
      pageStyle: {
        background: clip.asset_metadata.caption_styles?.background_style_color
          ? tinycolor(clip.asset_metadata.caption_styles.background_style_color).setAlpha(0.6).toHex8String()
          : undefined,
        paddingLeft: '16px',
        paddingRight: '16px',
        borderRadius: '8px',
        color: clip.asset_metadata.magicLayout?.textColor || TEXT_COLOR_DEFAULT
      },
      wordStyle: {
        active: {
          textShadow: getCaptionStyles(CaptionStyle.Outline, 1, undefined, OUTLINE_COLOR_DEFAULT).textShadow,
          color: clip.asset_metadata.highlight_type === 'text' ? clip.asset_metadata.text_highlight_color : undefined
        },
        inactive: {
          textShadow: getCaptionStyles(CaptionStyle.Outline, 1, undefined, OUTLINE_COLOR_DEFAULT).textShadow,
          color: TEXT_COLOR_DEFAULT
        }
      },
      letterStyle: undefined,

      // Deprecated fields
      scale: clip.asset_metadata.caption_styles?.scale || 1,
      animation: clip.asset_metadata.caption_styles?.animation || CaptionStyle.Outline,
      background_style_color: clip.asset_metadata.caption_styles?.background_style_color
    };
  }
}

function updateClipResponse({
  clip,
  clipConfigUpdateOverride,
  facialRecognitionToDefaultPosition,
  isSharedPage
}: {
  clip: Clip;
  clipConfigUpdateOverride?: boolean;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  isSharedPage?: boolean;
}): Clip {
  updateClipDefaultProperties(clip, facialRecognitionToDefaultPosition);
  const layoutStatus = getLayoutAvailabilityStatus(clip.asset_metadata.layout, clip.layout_status);
  const isLayoutProcessing = layoutStatus === 'PROCESSING';
  const shouldDeleteConfig = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Delete_Config];

  const doesClipNeedConfigUpdate =
    clipConfigUpdateOverride ||
    (shouldDeleteConfig
      ? !doesClipHavePlayerConfig(clip)
      : !clip.asset_metadata.config || !doesClipHavePlayerConfig(clip));

  // ! Please check with @AshwinBhatkal before updating this variable
  clipsNeedingConfigUpdateStore.update(data => {
    data[clip.id] = doesClipNeedConfigUpdate;
    return data;
  });

  if (isLayoutProcessing !== !!clip.asset_metadata.isLayoutProcessing) {
    clipsNeedingConfigUpdateStore.update(data => {
      data[clip.id] = true;
      return data;
    });
    clip.asset_metadata.isLayoutProcessing = isLayoutProcessing;
  }

  // remotion config update will be handled as part of the clipsNeedingConfigUpdateStore action
  updateClipWithId(clip.id, clip, true, true, isSharedPage);

  initHistory(clip.id, clip.asset_metadata);

  return clip;
}

const getDefaultAppliedStylesToRSRecordings = (clip: Clip) => {
  const theme_obj = clip.content?.project?.theme_obj || {};
  const {
    fit,
    border,
    layout,
    stage_background_type,
    stage_background,
    primary_text_color,
    name_card_text_color,
    name_card_background,
    name_card_visibility_toggle,
    name_card_font_url,
    name_card_border_radius
  } = theme_obj;

  // By default we show speaker labels in recording studio
  const showSpeakerLabels = name_card_visibility_toggle === undefined || name_card_visibility_toggle === 'visible';
  const backgroundImage =
    stage_background_type === 'Image' ? stage_background.replace('url(', '').replace(')', '') : undefined;
  const backgroundColor = stage_background_type === 'Color' ? rgbaToHex(stage_background) : BACKGROUND_COLOR_DEFAULT;

  const showCaptions = !!clip.asset_metadata.subtitle;

  return {
    ...clip,
    asset_metadata: {
      ...clip.asset_metadata,
      layout: layout || 'GRID',
      fit: fit || 'FIT',
      hide_borders: !border,
      magicLayout: {
        ...clip.asset_metadata.magicLayout,
        textColor:
          (showCaptions
            ? clip.asset_metadata.magicLayout?.textColor
            : showSpeakerLabels
              ? rgbaToHex(name_card_background)
              : rgbaToHex(primary_text_color)) || TEXT_COLOR_DEFAULT,
        backgroundImage,
        backgroundColor,
        showSpeakerLabels
      },
      speaker_label_styles: migrateSpeakerLabelStyles({
        font_location: name_card_font_url || FONT_DEFAULT_VALUE.url,
        text_color: rgbaToHex(name_card_text_color),
        background_color: rgbaToHex(name_card_background),
        border_radius: name_card_border_radius
      })
    }
  };
};

export const addScreenshareToVideoSpeakerDetails = (
  clip: Clip,
  videoSpeakerDetails: Record<string, { speakerKey: string; videoType: RecordingVideoType }>
) => {
  if (isScreenShareEnabledForCustomUploadClips(clip)) {
    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];
    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      videoSpeakerDetails[UPLOAD_SCREENSHARE_ID] = {
        speakerKey: 'screen-share',
        videoType: RecordingVideoType.SCREENSHARE
      };
    }
  }
};

const prefetchDetailsForRemotionConfig = async ({
  clip,
  isSharedPage,
  isSavedSearchPage,
  isSmartEventsPage
}: {
  clip: Clip;
  isSharedPage: boolean;
  isSavedSearchPage: boolean;
  isSmartEventsPage: boolean;
}) => {
  try {
    if (clip.asset_metadata && isScreenShareEnabledForCustomUploadClipsFeatureFlag() && isClipFromUploads(clip)) {
      await getSpeakersAnalysis(clip.id, clip.asset_metadata.start, clip.asset_metadata.visible_face_ids || [], false);
    }
  } catch (error) {
    window.logger?.error('Error fetching speakers analysis', error);
  }

  if (!doesClipWorkOnAlignedStreams(clip)) {
    const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
    const videoSpeakerDetails = generateVideoIdSpeakerMapFromSpeakers(speakerDetails);

    addScreenshareToVideoSpeakerDetails(clip, videoSpeakerDetails);

    updateVideoToSpeakerDetailsStore({
      broadcastId: getClipBroadcastId(clip)!,
      updatedSpeakerDetails: videoSpeakerDetails
    });
  }
  if (
    doesClipWorkOnAlignedStreams(clip) &&
    !isSharedPage &&
    getAlignedRecordings(getClipBroadcastId(clip)).length === 0
  ) {
    const queryParams: ListActivityAPIQueryParams = isLiveEventRemotionWithPreviewAndAlignedStream(clip)
      ? { status: 'complete' }
      : {};
    await fetchAlignedRecordings({
      broadcast_id: clip.broadcast,
      usePureCloudRecording: clip.content.use_pure_cloud_recording,
      queryParams
    });
  }

  if (isSavedSearchPage || isSmartEventsPage) {
    // for both smart events and saved search page, SessionDetails (Parent TranscriptContext) doesn't exist.
    const broadcastId = clip.broadcast;
    const eventId = clip.event;
    const cacheSuffix = Math.random().toString();
    const [transcriptResult, sessionSpeakers] = await Promise.allSettled([
      getTranscript(cacheSuffix, eventId!, broadcastId!),
      loadSpeakers(getSpeakersPayloadParams(broadcastId!, !isClipFromUploads(clip)))
    ]);

    // Extract values and handle potential failures
    const transcriptData =
      transcriptResult.status === 'fulfilled'
        ? transcriptResult.value
        : {
            transcript: [],
            fullTranscript: []
          };
    const speakersData = sessionSpeakers.status === 'fulfilled' ? sessionSpeakers.value : {};

    const speakers = formSpeakerDetails(speakersData);
    const { speakers: speakersWithSpeakingSlots } = populateSpeakersWithSpeakingSlots({
      speakers,
      transcriptResult: transcriptData,
      broadcastId: clip.content.id
    });
    updateSpeakerDetailsStoreForBroadcast(getClipBroadcastId(clip)!, speakersWithSpeakingSlots);
    if (!doesClipWorkOnAlignedStreams(clip)) {
      const videoSpeakerDetails = generateVideoIdSpeakerMapFromSpeakers(speakersWithSpeakingSlots);
      addScreenshareToVideoSpeakerDetails(clip, videoSpeakerDetails);
      updateVideoToSpeakerDetailsStore({
        broadcastId: getClipBroadcastId(clip)!,
        updatedSpeakerDetails: videoSpeakerDetails
      });
    }
  }
};

const initializeDeletesAndMaxVideoDuration = async ({ clip }: { clip: Clip }) => {
  let hasInitializedMaxVideoDuration = false;
  if (isNotFullLengthClip(clip)) {
    if (!isNumber(clip.asset_metadata.maxVideoDuration)) {
      const recordings = getAlignedRecordings(getClipBroadcastId(clip));
      const firstRecording = recordings?.[0];
      const originalVideoUri = clip.content.preview;
      if (firstRecording || originalVideoUri) {
        const videoUri = firstRecording?.preview_url ?? firstRecording?.mp4_url ?? originalVideoUri;
        const duration = await getVideoDurationFromSrc(videoUri);
        clip.asset_metadata.maxVideoDuration = duration;
        hasInitializedMaxVideoDuration = true;
      }
    }

    const { start, end, maxVideoDuration, deletes: existingDeletes } = clip.asset_metadata;

    const clipMandatoryDeletes: Clip['asset_metadata']['deletes'] = {};
    if (clip.asset_metadata.start > 0) {
      clipMandatoryDeletes[0] = {
        bounds: [0, start],
        prev_end_time: 0,
        next_start_time: start
      };
    }
    if (isNumber(maxVideoDuration) && end < maxVideoDuration) {
      clipMandatoryDeletes[end] = {
        bounds: [end, maxVideoDuration],
        prev_end_time: end,
        next_start_time: maxVideoDuration
      };
    }
    const overlappingDeletes = { ...(existingDeletes ?? {}), ...clipMandatoryDeletes };
    const finalDeletes = mergeDeletes(overlappingDeletes);
    clip.asset_metadata.deletes = finalDeletes;
  }
  return { hasInitializedMaxVideoDuration };
};

export const isPlayerConfigStale = (clip: Clip) => {
  const compositeSegments = getAllCompositeSegments(clip);
  if (!compositeSegments || compositeSegments.length === 0) {
    return false;
  }

  let isStale = false;

  if (isOriginalLayoutAvailable(clip)) {
    const originalVideoPresent = compositeSegments?.[0]?.videos?.find(video => video.isOriginalVideo);

    if (!originalVideoPresent) {
      logMigrationEvent(clip, 'ORIGINAL_VIDEO_NOT_PRESENT');
      isStale = true;
    } else if (originalVideoPresent.details.preview !== clip.content.preview) {
      logMigrationEvent(clip, 'ORIGINAL_VIDEO_PREVIEW_CHANGED');
      isStale = true;
    }
  }

  if (!doesClipWorkOnAlignedStreams(clip) && isClipAudiogram(clip)) {
    const moreThanOneVideo = compositeSegments.some(compositeSegment => compositeSegment.videos.length > 1);
    const mutedVideoIsHydrated = compositeSegments.some(compositeSegment =>
      compositeSegment.layoutChanges.some(layout => layout?.mutedVideosId?.length > 0)
    );
    if (moreThanOneVideo && !mutedVideoIsHydrated) {
      isStale = true;
    }
  }

  if (!compositeSegments.some(compositeSegment => compositeSegment.playbackAdjustedScenes)) {
    isStale = true;
  }

  return isStale;
};

const isConfigGenerationNeeded = ({
  clip,
  isSharedPage,
  updatePlayerConfigWithNewVideoIds,
  hasInitializedMaxVideoDuration,
  hasUpdatedIntroOutroDurations,
  hasMigratedClip
}: {
  clip: Clip;
  isSharedPage: boolean;
  updatePlayerConfigWithNewVideoIds: boolean;
  hasInitializedMaxVideoDuration: boolean;
  hasUpdatedIntroOutroDurations: boolean;
  hasMigratedClip: boolean;
}) => {
  if (isSharedPage) {
    return false;
  }
  const isPlayerConfigNeededAndMissing = !doesClipHavePlayerConfig(clip);
  if (isPlayerConfigNeededAndMissing) {
    return true;
  }

  const isUsingRemotionAndInitConfigFlagEnabled =
    featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Init_New_Player_Config];

  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  const allCompositeSegments = getAllCompositeSegments(clip);
  const allCompositeHaveSegmentMetadata = allCompositeSegments.every(
    compositeSegment => compositeSegment.segment_metadata
  );
  if (isSplitsEnabled && !allCompositeHaveSegmentMetadata) {
    return true;
  }
  if (!isSplitsEnabled && allCompositeHaveSegmentMetadata) {
    return true;
  }

  if (isUsingRemotionAndInitConfigFlagEnabled) {
    return true;
  }

  if (updatePlayerConfigWithNewVideoIds) {
    return true;
  }

  if (hasInitializedMaxVideoDuration && !isSharedPage) {
    return true;
  }

  if (hasUpdatedIntroOutroDurations && !isSharedPage) {
    return true;
  }

  if (isPlayerConfigStale(clip)) {
    return true;
  }

  if (hasMigratedClip) {
    return true;
  }

  return false;
};

const shouldFetchSceneOverrides = ({ clip }: { clip: Clip }) => {
  if (!isFullLengthClip(clip) && clip.asset_metadata.scenes?.length && clip.asset_metadata.scenes.length > 0) {
    return false;
  }

  return true;
};

const shouldUseFaceRecToDefaultPosition = () => {
  return featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Face_Rec_To_Default_Position];
};

const updateIntroOutroDurationHandler = async (clip: Clip) => {
  let finalClip = clip;
  let hasUpdatedIntroOutroDurations = false;
  {
    const { updatedMetadata, hasUpdated } = await updateIntroOutroDurationsIfNeeded(clip.asset_metadata);
    hasUpdatedIntroOutroDurations = hasUpdated;
    if (hasUpdatedIntroOutroDurations) {
      finalClip = { ...clip, asset_metadata: updatedMetadata as ClipMetadata };
    }
  }
  return { hasUpdatedIntroOutroDurations, clip: finalClip };
};

const logMigrationEvent = (clip: Clip, name: string) => {
  window.analytics?.track('ContentLabEvent', {
    name,
    isSuccess: true,
    ...getClipAnalyticsPayloadV2(clip)
  });
};

const migrateClip = ({ clip, isSharedPage }: { clip: Clip; isSharedPage: boolean }) => {
  if (isSharedPage) {
    return { hasMigratedClip: false, clip };
  }
  const speakerLayoutIsNotSupported =
    canUseNewTimelineForOldLiveEvent({ clip }) || canUseNewTimelineForUnmigratedLiveEvent(clip);
  if (speakerLayoutIsNotSupported && isSpeakerLayout(clip.asset_metadata.layout)) {
    const oldLayout = clip.asset_metadata.layout;
    const oldLayoutHumanReadableName = CLIP_CUSTOMIZER_LAYOUT_TYPES.find(layout => layout.value === oldLayout)?.label;
    showInfoToast(`We've changed your clip layout from ${oldLayoutHumanReadableName} to Original.`);
    logMigrationEvent(clip, 'CLIP_MIGRATED_TO_ORIGINAL_LAYOUT');
    clip.asset_metadata.layout = 'DEFAULT';
    if (doesClipHaveScenes(clip)) {
      // Ideally we will never go here, because scenes won't exist until new timeline is mounted
      logMigrationEvent(clip, 'FOUND_SCENES_IN_CLIP_WILL_BE_DISCARDED');
      clip.asset_metadata.scenes = undefined;
    }
    return { hasMigratedClip: true, clip };
  }
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip) && isLiveEventWithManualMigration(clip.content)) {
    // Old event migration complete
    const playerConfig = getClipReadOnlyPlayerConfig(clip);
    if (!playerConfig) {
      if (doesClipHaveScenes(clip)) {
        clip.asset_metadata.scenes = undefined;
        logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_SCENES_EXISTS_WITHOUT_NEW_TIMELINE');
        return { hasMigratedClip: true, clip };
      }
      logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_NO_PLAYER_CONFIG');
      return { hasMigratedClip: true, clip };
    }
    const compositeSegment = getCompositeSegment(playerConfig);
    if (!compositeSegment) {
      throw new Error('Player config exists without composite segment');
    }
    const originalExists = compositeSegment.videos.some(v => v.isOriginalVideo);
    if (!originalExists) {
      logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ORIGINAL_NOT_FOUND');
      if (doesClipHaveScenes(clip)) {
        logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ORIGINAL_NOT_FOUND_SCENES_DISCARDED');
        // need to do this because video crop might have existed on basis of video ids in scenes
        clip.asset_metadata.scenes = undefined;
      }
      return { hasMigratedClip: true, clip };
    }
    const segmentVideoUri = compositeSegment.videos.map(v => v.details.src);
    const uniqueVideoUris = [...new Set(segmentVideoUri)];
    if (uniqueVideoUris.length === 1 && segmentVideoUri.length > 1) {
      logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ALL_VIDEOS_POINTING_TO_SAME_SOURCE');
      // all videos were pointing to same original source, migrate to start using individual recording source
      // ⚠️ can get executed repeatedly if 0 individual recordings/speakers
      if (doesClipHaveScenes(clip)) {
        logMigrationEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ALL_VIDEOS_POINTING_TO_SAME_SOURCE_SCENES_DISCARDED');
        // need to do this because video crop might have existed on basis of video ids in scenes
        clip.asset_metadata.scenes = undefined;
      }
      return { hasMigratedClip: true, clip };
    }
    logMigrationEvent(clip, 'LIVE_EVENT_NO_NEED_TO_RE_GRENERATE_PLAYER_CONFIG');
  }
  if (doesClipHaveScenes(clip)) {
    const { updatedScenes, rectified } = rectifyScenes(clip);
    if (rectified) {
      // eslint-disable-next-line no-console
      console.log('Scenes Rectified');
      logMigrationEvent(clip, 'SCENES_RECTIFIED');
      clip.asset_metadata.scenes = updatedScenes;
      return { hasMigratedClip: true, clip };
    }
  }
  return { hasMigratedClip: false, clip };
};

export async function initClip(
  id: string,
  {
    shared = false,
    isSavedSearchPage = false,
    isSmartEventsPage = false
  }: { shared?: boolean; isSavedSearchPage: boolean; isSmartEventsPage: boolean }
): Promise<Clip> {
  let clip: Clip = await loadClip(id);
  await waitForClipToBeReady({ clip, isSharedPage: shared });

  await prefetchDetailsForRemotionConfig({ clip, isSharedPage: shared, isSavedSearchPage, isSmartEventsPage });

  const { hasInitializedMaxVideoDuration } = await initializeDeletesAndMaxVideoDuration({ clip });

  const { hasMigratedClip, clip: migratedClip } = migrateClip({ clip, isSharedPage: shared });
  clip = migratedClip;

  const { hasUpdatedIntroOutroDurations, clip: updatedDurationClip } = await updateIntroOutroDurationHandler(clip);
  clip = updatedDurationClip;

  const updatePlayerConfigWithNewVideoIds = shouldUpdateVideoIdsInClip({ clip, isSharedPage: shared });

  const updateNewPlayerConfig = isConfigGenerationNeeded({
    clip,
    isSharedPage: shared,
    updatePlayerConfigWithNewVideoIds,
    hasInitializedMaxVideoDuration,
    hasUpdatedIntroOutroDurations,
    hasMigratedClip
  });

  let facialRecognitionToDefaultPosition: SpeakerFaceCoordinatesResponse | undefined = undefined;

  if (updateNewPlayerConfig) {
    let sceneOverrides: SceneChanges | undefined = undefined;
    if (isClipFromRecordingStudio(clip) && !isClipAudiogram(clip) && !updatePlayerConfigWithNewVideoIds) {
      clip = getDefaultAppliedStylesToRSRecordings(clip);

      if (shouldFetchSceneOverrides({ clip })) {
        // TODO: @@HiteshChoudhary0098 this needs to be revisited. What backend stopped sending types for transcript mapping.
        // @ts-ignore
        sceneOverrides = (await getSceneChanges({ id: clip.broadcast })) as SceneChanges;
      }
    }

    if (updatePlayerConfigWithNewVideoIds) {
      clip = updateScenesWithNewVideoIds(clip);
    }

    if ((sceneOverrides || isClipLiveEvent(clip)) && shouldUseFaceRecToDefaultPosition()) {
      facialRecognitionToDefaultPosition = await cronSpeakerFaceCoordinatesCreate({
        body: {
          content_id: clip.content.id,
          project_id: clip.content.project.id
        }
      });
    }

    updateClipDefaultProperties(clip, facialRecognitionToDefaultPosition);
    const newPlayerConfig = getPlayerConfig({
      clip,
      sceneOverrides,
      shouldUpdatePlayerConfig: true,
      facialRecognitionToDefaultPosition
    });

    clip = {
      ...clip,
      asset_metadata: {
        ...clip.asset_metadata,
        player_config: newPlayerConfig
      }
    };
    if (sceneOverrides) {
      const allCompositeSegments = getAllCompositeSegments(clip);
      const firstSegment = allCompositeSegments[0];
      clip.asset_metadata.scenes = firstSegment?.scenes || clip.asset_metadata.scenes;
    }
  }

  return updateClipResponse({
    clip,
    clipConfigUpdateOverride: updateNewPlayerConfig,
    facialRecognitionToDefaultPosition,
    isSharedPage: shared
  });
}

function mapVideoIdsToAlignedRecordings(videoIds: string[], alignedRecordingsStore: AlignedRecordings[]): string[] {
  return videoIds
    .map(id => {
      const recording = alignedRecordingsStore.find(r => r.associatedCloudRecordingId === id);

      return recording?.id;
    })
    .filter(a => a !== undefined);
}

function updateScenesWithNewVideoIds(clip: Clip): Clip {
  const recordings = getAlignedRecordings(getClipBroadcastId(clip));

  const existingScenes = clip.asset_metadata.scenes;
  if (!existingScenes || existingScenes.length === 0) {
    return clip;
  }

  const newScenes = existingScenes.map(scene => ({
    ...scene,
    details: {
      ...scene.details,
      visible_video_ids: mapVideoIdsToAlignedRecordings(scene.details.visible_video_ids, recordings),
      all_video_ids: recordings.map(recording => recording.id)
    }
  }));

  return {
    ...clip,
    asset_metadata: {
      ...clip.asset_metadata,
      scenes: newScenes
    }
  };
}

interface SaveClipChangesProps {
  clipId: string;
  shouldGenerateThumbnail?: boolean;
  shouldAddToHistory?: boolean;
  isEditedState?: boolean;
  isTranslatedCaptionEdited?: boolean;
  skipRemotionUpdate?: boolean;
  onFailure?: ({ error, clip }: { error: Error; clip: Clip }) => void;
}

/**
 * @deprecated This function is deprecated and will be removed in future releases. Use updateClipData instead.
 */
export const saveClipChanges: (props: SaveClipChangesProps) => void = debounce(
  ({
    clipId,
    shouldGenerateThumbnail = true,
    shouldAddToHistory = true,
    isEditedState = true,
    isTranslatedCaptionEdited = false,
    skipRemotionUpdate,
    onFailure
  }: SaveClipChangesProps) => {
    const clip = Object.assign({}, currentClip.getSnapshot()[clipId]);
    if (isEmptyObject(clip)) {
      return;
    }

    if (shouldGenerateThumbnail) {
      generateThumbnail(clipId);
    }

    if (shouldAddToHistory) {
      updateHistory(clipId, clip.asset_metadata);
    }

    clip.asset_metadata = {
      ...clip.asset_metadata,
      is_edited: isEditedState,
      isLayoutProcessing: getLayoutAvailabilityStatus(clip.asset_metadata.layout, clip.layout_status) === 'PROCESSING'
    };

    const clipUpdatePromise = updateClipConfig({ clipId, clip, isTranslatedCaptionEdited, skipRemotionUpdate });

    clipUpdatePromise.then(value => {
      templateLoader.set(() => false);
      updateClipWithId(clipId, { updated_at: value.updated_at }, false, true);
    });

    clipUpdatePromise.catch(error => {
      window.logger?.error('Error updating clip', error);
      window.analytics?.track('ContentLabEvent', {
        name: 'ContentEdited',
        isSuccess: false,
        ...getClipAnalyticsPayload(clip)
      });
      if (onFailure) {
        onFailure({ clip, error });
      }
    });
  }
);

export function deleteWords(clipId: string, toDelete: DeletedRange) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE_WORDS,
    startTime: toDelete.bounds[0],
    endTime: toDelete.bounds[1],
    deleteType: toDelete.deleteType || 'MANUAL'
  });
}

export function toggleCaptions(clipId: string, enabled: boolean) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.TOGGLE_CAPTIONS,
    enabled
  });
}

export function toggleHideBorders(clipId: string, showBorders: boolean) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_BORDERS_VISIBILITY,
    hideBorders: showBorders
  });
}

export function toggleSpeakerLabels(clipId: string, enabled: boolean, shouldUpdateConfig: boolean = true) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABELS,
      showSpeakerLabels: enabled
    },
    {
      skipBackendUpdate: !shouldUpdateConfig
    }
  );

  const useAnimatedSpeakerCards = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Animated_Speaker_Cards];
  if (!enabled && useAnimatedSpeakerCards) {
    updateSpeakerLabelStyles(clipId, {
      transitions: SPEAKER_LABEL_DEFAULT_TRANSITIONS
    });
  }
}

export function selectMagicLayout(clipId: string, magicLayoutType: LayoutType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
    layout: magicLayoutType
  });
}

export function changeSize(clipId: string, size: SizeType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SIZE,
    size
  });
}

export function changeCaptionsPosition({
  clipId,
  caption_positions,
  skipUpdate = false
}: {
  clipId: string;
  caption_positions: CaptionsPosition;
  skipUpdate?: boolean;
}) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTIONS_POSITION,
      captionPositions: caption_positions
    },
    {
      skipBackendUpdate: skipUpdate
    }
  );
}

export function updateCaptionStyles(
  clipId: string,
  captionStyles: Partial<CaptionStyles>,
  shouldAddToHistory: boolean = true
) {
  updateClipData(
    {
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTION_STYLES,
      captionStyles
    },
    {
      skipBackendUpdate: !shouldAddToHistory
    }
  );
}

export function updateCaptionPreset(clipId: string, presetName: CaptionPresetName) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CAPTION_PRESET,
    presetName
  });
}

export function updateIntroOutro(clipId: string, key: 'intro' | 'outro', { url, duration }: UpdateIntroOutroProps) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
    type: SegmentType.VIDEO,
    id: key,
    url: url as string,
    duration: duration as number
  });
}

export function updateStartEndTime(clipId: string, key: 'start' | 'end', value?: number, duration?: number) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
    type: SegmentType.COMPOSITE,
    compositeId: 'compositeId',
    trackId: MAIN_TRACK_ID,
    startTime: key === 'start' ? value : undefined,
    endTime: key === 'end' ? value : undefined,
    duration: key === 'end' && duration ? duration : undefined
  });
}

export function updateCaptionFontLocation(clipId: string, path: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_FONT_LOCATION,
    path
  });
}

export function updateSpeakerLabelStyles(
  clipId: string,
  partialSpeakerLabelStyles: Partial<Clip['asset_metadata']['speaker_label_styles']>
) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_SPEAKER_LABEL_STYLES,
    speakerLabelsStyles: partialSpeakerLabelStyles
  });
}

export function updateHighlightType(clipId: string, highlightType: HighlightType) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      highlightType
    }
  });
}

export function updateHighlightColor(clipId: string, highlightColor?: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      highlightColor
    }
  });
}

export function updateTextHighlightColor(clipId: string, highlightColor?: string) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_WORD_HIGHLIGHT,
    updatePayload: {
      textHighlightColor: highlightColor
    }
  });
}

export function applyTemplate(clipId: string, template: PreviewTemplate) {
  const clip = currentClip.getSnapshot()[clipId];

  let updatedScenes: Scenes = [];

  if (clip.asset_metadata.scenes && clip.asset_metadata.scenes.length > 0) {
    updatedScenes = clip.asset_metadata.scenes;

    if (template.layout === 'AUDIOGRAM' && updatedScenes.length > 0) {
      const firstScene = updatedScenes[0];
      const lastScene = updatedScenes[updatedScenes.length - 1];

      updatedScenes = [
        {
          ...firstScene,
          toFrame: lastScene.toFrame,
          details: {
            ...firstScene.details,
            fitMode: template.fit,
            layout: template.layout as LayoutTypeV2
          }
        }
      ];
    } else {
      updatedScenes = clip.asset_metadata.scenes.map(scene => ({
        ...scene,
        details: {
          ...scene.details,
          fitMode: template.fit,
          layout: template.layout as LayoutTypeV2
        }
      }));
    }
  }

  const hasCompleteIntroOutroInformation =
    (template.intro ? !!template.introDuration : true) && (template.outro ? !!template.outroDuration : true);
  const useAsyncClipUpdate = !hasCompleteIntroOutroInformation;
  const clipUpdateFunction = useAsyncClipUpdate ? updateClipMetadataWithIdAsync : updateClipMetadataWithId;

  clipUpdateFunction(clipId, {
    ...pick(template, propertiesToCompareV1 as (keyof PreviewTemplate)[]),
    ...(updatedScenes.length > 0 ? { scenes: updatedScenes } : {}),
    is_edited: false,
    template_id: template.id,
    speaker_label_styles: migrateSpeakerLabelStyles(template.speaker_label_styles)
  });

  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  if (isSplitsEnabled) {
    applySplitChangesFromTemplate(clip, template);
  }

  const captionsPositions = template.caption_positions;
  if (captionsPositions) {
    const { x, y, width, height } = captionsPositions;

    EventBus.dispatch(CustomEvents.UpdateCaptionsPosition, {
      left: parseFloat(x as unknown as string) + '%',
      top: parseFloat(y as unknown as string) + '%',
      width: parseFloat(width as unknown as string) + '%',
      height: parseFloat(height as unknown as string) + '%'
    });
  }

  templateLoader.set(() => true);
  saveClipChanges({ clipId, shouldGenerateThumbnail: true, shouldAddToHistory: true, isEditedState: false });
  trackClipUpdate(clipId, 'LAYOUT_CHANGE');
}

function applySplitChangesFromTemplate(clip: Clip, template: PreviewTemplate) {
  if (clip.asset_metadata.player_config) {
    const track = getSegmentsTrack(clip.asset_metadata.player_config);
    const introTrack = track?.segments.find(segment => segment.id === 'intro');
    const outroTrack = track?.segments.find(segment => segment.id === 'outro');
    if (introTrack) {
      updateClipData({
        clipId: clip.id,
        actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
        id: introTrack.id
      });
    }
    if (outroTrack) {
      updateClipData({
        clipId: clip.id,
        actionType: CLIP_UPDATE_TYPE.REMOVE_SEGMENT_TRACK,
        id: outroTrack.id
      });
    }
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
      layout: template.layout
    });
  }

  if (template.intro) {
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
      type: SegmentType.VIDEO,
      id: 'intro',
      url: template.intro as string,
      duration: template.introDuration as number
    });
  }
  if (template.outro) {
    updateClipData({
      clipId: clip.id,
      actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
      type: SegmentType.VIDEO,
      id: 'outro',
      url: template.outro as string,
      duration: template.outroDuration as number
    });
  }
}

export function getActiveCaptionsBackgroundColor(
  clipMetadata: ClipMetadata | PreviewTemplate,
  highlightType?: HighlightType,
  backgroundColor?: string
): string | undefined {
  const highlight = highlightType || clipMetadata.highlight_type;
  const bgColor = backgroundColor || clipMetadata.word_highlight_color;
  return highlight === 'box' ? bgColor : 'transparent';
}

export function getInactiveCaptionsOpacityClass(
  clipMetadata: ClipMetadata | PreviewTemplate,
  highlightType?: HighlightType
): number {
  const highlight = highlightType || clipMetadata.highlight_type;
  switch (highlight) {
    case 'text':
      return clipMetadata.caption_styles?.animation === 'Background' ? 1 : 0.5;
    default:
      return 1;
  }
}

// !! Hitesh Need to check where is the use case
export function updateVisibleFaceIdsInClip(clipId: string, visible_face_ids: number[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_FACE_IDS,
    visibleFaceIds: visible_face_ids
  });
}

export function updateVisibleSpeakers(clipId: string, visible_speakers: SpeakerWithDetails[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_SPEAKERS,
    visibleSpeakers: visible_speakers
  });
}

export function updateVisibleVideoIds(clipId: string, visible_video_ids: string[]) {
  updateClipData({
    clipId,
    actionType: CLIP_UPDATE_TYPE.UPDATE_VISIBLE_VIDEO_IDS,
    visibleVideoIds: visible_video_ids
  });
}

/**
 * @deprecated This function is deprecated and should not be used in new implementations.
 */
export function updateClipWithId(
  clipId: string,
  newClipData: Partial<Clip>,
  resetData?: boolean,
  skipRemotionUpdate?: boolean,
  isSharedPage?: boolean
) {
  currentClip.update(data => {
    const clip = data[clipId] ? current(data[clipId]) : undefined;

    const newClip = resetData
      ? newClipData
      : {
          ...clip,
          ...newClipData
        };

    const normalizedClip = normalizeClipConfigForDeleteFlag(newClip as Clip);

    return getFinalClipWithRemotionConfig({
      allClips: data,
      clip: normalizedClip as Clip,
      skipRemotionUpdate,
      shouldUpdatePlayerConfig: true,
      isSharedPage
    });
  });
}

async function updateIntroOutroDurationsIfNeeded(
  metadata: Partial<Clip['asset_metadata']>
): Promise<{ updatedMetadata: Partial<Clip['asset_metadata']>; hasUpdated: boolean }> {
  const missingIntroDuration = metadata.intro && !metadata.introDuration;
  const missingOutroDuration = metadata.outro && !metadata.outroDuration;

  if (!missingIntroDuration && !missingOutroDuration) {
    return { updatedMetadata: metadata, hasUpdated: false };
  }

  // Create promises for parallel execution
  const promises: Promise<{ type: 'intro' | 'outro'; duration: number }>[] = [];

  if (missingIntroDuration) {
    promises.push(getVideoDurationFromSrc(metadata.intro as string).then(duration => ({ type: 'intro', duration })));
  }

  if (missingOutroDuration) {
    promises.push(getVideoDurationFromSrc(metadata.outro as string).then(duration => ({ type: 'outro', duration })));
  }

  // Execute all promises in parallel with allSettled
  const results = await Promise.allSettled(promises);

  // Process results
  let introDuration: number | undefined;
  let outroDuration: number | undefined;

  for (const result of results) {
    if (result.status === 'fulfilled') {
      if (result.value.type === 'intro') {
        introDuration = result.value.duration;
      } else if (result.value.type === 'outro') {
        outroDuration = result.value.duration;
      }
    }
    // If status is 'rejected', we simply skip that duration (it remains undefined)
  }

  return {
    updatedMetadata: {
      ...metadata,
      ...(missingIntroDuration && introDuration !== undefined && { introDuration }),
      ...(missingOutroDuration && outroDuration !== undefined && { outroDuration })
    },
    hasUpdated: true
  };
}

export function updateClipMetadataWithId(
  clipId: string,
  newMetadata: Partial<Clip['asset_metadata']>,
  shouldUpdatePlayerConfig?: boolean
) {
  currentClip.update(data => {
    const clip = current(data[clipId]);
    const normalizedMetadata = normalizeClipMetadataConfigForDeleteFlag(newMetadata);

    const newClip = {
      ...clip,
      asset_metadata: {
        ...clip.asset_metadata,
        ...normalizedMetadata
      }
    };

    return getFinalClipWithRemotionConfig({
      allClips: data,
      clip: newClip as Clip,
      skipRemotionUpdate: false,
      shouldUpdatePlayerConfig: shouldUpdatePlayerConfig ?? true
    });
  });
}

/**
 * ! Use this wisely
 * This is only a hack to get things to work when applying a template
 */

export async function updateClipMetadataWithIdAsync(
  clipId: string,
  newMetadata: Partial<Clip['asset_metadata']>,
  shouldUpdatePlayerConfig?: boolean
) {
  // Update intro/outro durations if needed
  const { updatedMetadata } = await updateIntroOutroDurationsIfNeeded(newMetadata);

  updateClipMetadataWithId(clipId, updatedMetadata, shouldUpdatePlayerConfig);
}

export function overwriteAllClips(newClips: Clip[]) {
  const currentClipData = currentClip.getSnapshot();

  allClips.set(() => {
    return newClips.map(clip => {
      const currentClip = currentClipData[clip.id];
      // Data that should not be overwritten on clip list reload
      if (currentClip?.asset_metadata) {
        return {
          ...clip,
          asset_metadata: {
            ...clip.asset_metadata,
            captions: currentClip.asset_metadata.captions
          }
        };
      }

      return clip;
    });
  });
}

export function appendAllClips(clips: Clip[]) {
  allClips.update(data => [...data, ...clips]);
}

export function prependAllClips(clips: Clip[]) {
  allClips.update(data => [...clips, ...data]);
}

export function clearAllClips() {
  allClips.set(() => []);
}

export function updateClipInList(updatedClip: Clip) {
  const normalizedClip = normalizeClipConfigForDeleteFlag(updatedClip);
  allClips.update(allClipsData => allClipsData.map(clip => (clip.id === normalizedClip.id ? normalizedClip : clip)));
}

export function onDuplicateClipSuccess(clip: ClipAssetResponse): Promise<void> {
  return new Promise(resolve => {
    prependAllClips([{ ...clip, isRemoved: false } as unknown as Clip]);

    setTimeout(() => {
      resolve();
    }, 200);
  });
}

export function removeClip(clipId: string): Promise<void> {
  return new Promise(resolve => {
    // Set isRemoved property to true, to animate removing, after short timeout - remove from list
    allClips.update(data =>
      data.map(clip => {
        return {
          ...clip,
          // 1st occurrence of condition check
          isRemoved: clipId === clip.asset_metadata.id || clipId === clip.id ? true : clip.isRemoved
        };
      })
    );

    setTimeout(() => {
      // 2nd occurrence of condition check
      allClips.update(data => data.filter(clip => clipId !== clip.asset_metadata.id && clipId !== clip.id));
      resolve();
    }, 500);
  });
}

export function setCanGenerateClips(canGenerate: boolean) {
  canGenerateClips.set(() => canGenerate);
}

export function trackClipUpdate(clipId: string, entityUpdated: string, payload?: Record<string, any>) {
  const edit_type = payload?.edit_type || getAnalyticsClipEditType(entityUpdated);

  window.analytics?.track('ContentLabEvent', {
    name: 'ContentEdited',
    entityUpdated,
    editType: edit_type,
    ...payload,
    ...getClipAnalyticsPayload(currentClip.getSnapshot()[clipId])
  });
}

export function setClipGeneratingCount(count: number) {
  clipGeneratingCount.set(() => count);
}

/**
 * @deprecated This function is deprecated and should not be used in new implementations.
 */
export function getFinalClipWithRemotionConfig({
  allClips,
  clip,
  skipRemotionUpdate,
  shouldUpdatePlayerConfig,
  isSharedPage
}: {
  allClips: CurrentClips;
  clip: Clip;
  skipRemotionUpdate?: boolean;
  shouldUpdatePlayerConfig?: boolean;
  isSharedPage?: boolean;
}) {
  const clipId = clip.id;

  if (!skipRemotionUpdate && !isSharedPage) {
    try {
      const remotionConfig = undefined;
      const player_config = getPlayerConfig({ clip, shouldUpdatePlayerConfig });

      return {
        ...allClips,
        [clipId]: {
          ...clip,
          asset_metadata: {
            ...clip.asset_metadata,
            remotionConfig,
            player_config
          }
        }
      };
    } catch (e) {
      window.logger?.error('Error while updating elements timeline', e);
    }
  }

  return {
    ...allClips,
    [clipId]: clip
  };
}

export const shouldUseRemotionConfig = (clip: Clip) => {
  const showCaptions = !!clip.asset_metadata?.subtitle;
  const showSpeakerLabels = !!clip.asset_metadata?.magicLayout?.showSpeakerLabels;

  return showCaptions || showSpeakerLabels;
};

export const isScreenShareEnabledForCustomUploadClipsFeatureFlag = () => {
  const featureFlags = featureFlagStore.getSnapshot();
  return !!featureFlags[FeatureFlagKeys.Use_CL_Screenshare_Detection];
};

export const isScreenShareEnabledForCustomUploadClips = (clip: Clip) => {
  return isScreenShareEnabledForCustomUploadClipsFeatureFlag() && isCustomUploadClip({ clip });
};

export const isLiveEventRemotionWithPreviewAndAlignedStream = (clip: Clip) => {
  return isClipLiveEvent(clip) && isClipAlignedStreamAvailable(clip);
};

/**
 * Use New timeline for live events where migration is impossible
 * @param content
 * @returns
 */
export const canUseNewTimelineForOldLiveEventContent = (content: MediaContent | MediaContentResponse) => {
  return (
    isLiveEventRecording(content.media_source_type, content.event_type, content.broadcast_type) &&
    !isAlignedStreamAvailable(content.individual_recording_status) &&
    !isRemotionMigrationPossibleForContent(content)
  );
};

export const canUseNewTimelineForOldLiveEvent = ({ clip }: { clip: Clip }) => {
  return canUseNewTimelineForOldLiveEventContent(clip.content);
};

export const isSplitsInTimelineEnabled = (clip: Clip) => {
  const originalLang = clip.asset_metadata.original_lang;
  const selectedLang = clip.asset_metadata.selected_lang;
  if (originalLang && selectedLang && originalLang !== selectedLang) {
    return false;
  }
  const allSegments = getAllSegments(clip);
  const hasMultipleCompositeSegments = allSegments.filter(segment => segment.type === SegmentType.COMPOSITE).length > 1;
  if (hasMultipleCompositeSegments) {
    return true;
  }

  const hasNonIntroOutroVideoSegment =
    allSegments.filter(
      segment => segment.type === SegmentType.VIDEO && segment.id !== 'intro' && segment.id !== 'outro'
    ).length > 0;
  if (hasNonIntroOutroVideoSegment) {
    return true;
  }

  return false;
};

export function requestCaptionTranslation(clipId: string, language: LanguageOption) {
  updateClipTranslationLoadingState(clipId, true);

  assetsClipTranslateClipCreate({
    body: {
      target_language: language.code,
      asset_id: clipId
    }
  })
    .then(() => {
      showSuccessToast(`Translation to ${language.name} is in progress, please wait...`);
    })
    .catch(() => {
      updateClipTranslationLoadingState(clipId, false);
      showErrorToast('Translation failed. Please try again.');
    });
}

// @abhineet TODO: This function needs to be revisited when caption translation is implemented
// !! TODO @HiteshChoudhary0098 the logic to update all the caption directly once we get the translation done will break config when we support multiple recording.
export async function updateClipCaption(clipId: string) {
  const clip: Clip = await loadClip(clipId);

  if (!clip) {
    return;
  }

  const { duration, excludes, captions } = computeCaptionsForClip(clip);
  updateClipMetadataWithId(clipId, {
    ...clip.asset_metadata,
    duration,
    excludes,
    captions
  });

  updateClipTranslationLoadingState(clipId, false);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });

  window.analytics?.track('ContentLabEvent', {
    name: 'ContentEdited',
    entityUpdated: 'CAPTIONS_TRANSLATION',
    editType: getAnalyticsClipEditType('CAPTIONS_TRANSLATION'),
    ...getClipAnalyticsPayload(clip),
    selectedLanguage: clip?.asset_metadata?.selected_lang
  });
}

export async function onLanguageChange(clipId: string, language: LanguageOption) {
  try {
    updateClipCaptionsLoadingState({ clipId, loading: true, forceRespectPayload: true });
    const clip: Clip = await loadClip(clipId, `&selected_lang=${language.code}`);
    updateClipCaptionsLoadingState({ clipId, loading: false, forceRespectPayload: true });
    const { duration, excludes, captions } = computeCaptionsForClip(clip);
    updateClipMetadataWithId(clipId, { ...clip.asset_metadata, duration, excludes, captions });
  } catch (error) {
    window.logger?.error('Error updating clip', error);
    showErrorToast("Updating the clip didn't go as planned. Please try refreshing the page.");
    window.analytics?.track('ContentLabEvent', {
      name: 'ContentLanguageChange',
      isSuccess: false
    });
  }

  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });

  window.analytics?.track('ContentLabEvent', {
    name: 'ContentEdited',
    entityUpdated: 'LANGUAGE_CHANGE',
    editType: getAnalyticsClipEditType('LANGUAGE_CHANGE'),
    ...getClipAnalyticsPayload(currentClip.getSnapshot()[clipId]),
    selectedLanguage: language.code
  });
}

/**
 * @deprecated - This function is only used for the old clip composition status.
 * To be removed with removal of Use_CL_Only_Local_Or_Cloud flag.
 */
export async function updateClipAfterFetchingLatestAlignedRecordings(
  broadcastId: string,
  clipId: string,
  queryParams: ListActivityAPIQueryParams
) {
  await fetchAlignedRecordings({ broadcast_id: broadcastId, queryParams });

  const existingScenes = currentClip.getSnapshot()[clipId].asset_metadata.scenes;
  const recordings = getAlignedRecordings(broadcastId);

  if (existingScenes) {
    const newScenes = existingScenes.map(scene => ({
      ...scene,
      details: {
        ...scene.details,
        visible_video_ids: mapVideoIdsToAlignedRecordings(scene.details.visible_video_ids, recordings),
        all_video_ids: mapVideoIdsToAlignedRecordings(scene.details.all_video_ids, recordings)
      }
    }));

    updateClipMetadataWithId(clipId, {
      scenes: newScenes
    });
  }

  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: false });

  // TODO: @hardik-saini to remove this once we have a loader for clip updates
  return new Promise<void>(resolve => {
    setTimeout(() => {
      resolve();
    }, 5000);
  });
}
// This function is used to check if all video ids in the composite are present in the alignedRecordings
// !! TODO  @HiteshChoudhary0098: This function to be updated in future when we support mulitple recordings
function shouldUpdateVideoIdsInClip({ clip, isSharedPage }: { clip: Clip; isSharedPage: boolean }) {
  if (isSharedPage) {
    return false;
  }

  if (!isClipFromRecordingStudio(clip)) {
    return false;
  }

  const recordings = getAlignedRecordings(getClipBroadcastId(clip));

  const playerConfig = clip.asset_metadata.player_config;
  if (playerConfig) {
    const compositeSegments = getAllCompositeSegments(clip);

    if (!compositeSegments || compositeSegments.length === 0) {
      return false;
    }

    const videoIdsInClip = compositeSegments.flatMap(compositeSegment =>
      compositeSegment.videos.map(video => video.id)
    );
    const videoIds = recordings.map(recording => recording.id);

    const areAllVideosInConfig = videoIdsInClip.every(videoId => videoIds.includes(videoId));
    if (!areAllVideosInConfig) {
      return true;
    }
  }

  return false;
}

export const doesClipWorkOnAlignedStreams = (clip: Clip) => {
  return isClipFromRecordingStudio(clip) || isLiveEventRemotionWithPreviewAndAlignedStream(clip);
};

export const areSpeakerLayoutsEnabled = (clip: Clip) => {
  const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];
  if (isClipFromUploads(clip)) {
    return speakersAnalysis && Object.keys(speakersAnalysis).length > 0;
  }

  return true;
};

/**
 * Use new timeline for unmigrated live event
 * @param content
 * @returns boolean
 */
export const canUseNewTimelineForUnmigratedLiveEventContent = (content: MediaContent | MediaContentResponse) => {
  return (
    isLiveEventRecording(content.media_source_type, content.event_type, content.broadcast_type) &&
    !isAlignedStreamAvailable(content.individual_recording_status) &&
    isLiveEventWithManualMigration(content) && // old
    isRemotionMigrationPossibleForContent(content)
  );
};

export const canUseNewTimelineForUnmigratedLiveEvent = (clip: Clip) => {
  return canUseNewTimelineForUnmigratedLiveEventContent(clip.content);
};

export const hasMoreThanOneCompositeSegment = (clip: Clip): boolean => {
  const allComposites = getAllCompositeSegments(clip);
  return allComposites.length > 1;
};

export const getIsClipUsingDefaultLanguage = (clip: Clip): boolean => {
  return (
    !clip.asset_metadata.original_lang ||
    !clip.asset_metadata.selected_lang ||
    clip.asset_metadata.original_lang === clip.asset_metadata.selected_lang
  );
};

export const canUseBrowserlessCaptions = () => {
  return featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Browserless_Captions] ?? false;
};
